import React, { useEffect, useRef, useState, useMemo } from "react";
import { DashboardLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import {
  carePlanQuestions,
  getCareplanValues,
  getPromise,
  getPromiseToast,
  groupQuestionsByCategory,
  formatDate,
  getErrToast,
  splitIcdCode,
} from "@/utils/helpers";
import {
  enrichCareplanChronicQuestions,
  fetchChronicQuestionsFromIcdApi,
} from "@/utils/icdChronicQuestions";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfMultiInput from "@/components/rhfFields/RhfMultiInput";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import RhfChecked from "@/components/rhfFields/RhfChecked";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useGetData } from "@/hooks/useGetData";
import { useIsMobile } from "@/hooks/use-mobile"

import { X, CheckCircle2, Download, IdCard } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AxiosInstance from "@/utils/AxiosInstance";
import { downloadBackendPdf } from "@/utils/downloadBackendPdf";
import { FormLabel } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/use-auth";
import { toast } from "sonner";
import { useFormContext, useWatch } from "react-hook-form";
import ViewCareplan from "./ViewCareplan";
import LoaderWrapper from "./LoaderWrapper";

// iused only for duplicate care plan
function filterSourceForDuplicateCareplanQuestions(
  sourceQuestions: any[] | undefined | null,
  selected: string[],
): any[] {
  // No source → return empty array
  if (!Array.isArray(sourceQuestions) || !sourceQuestions.length) {
    return [];
  }
  const selectedSet = new Set(
    selected.map((s) => String(s ?? "").trim()).filter(Boolean),
  );
  return sourceQuestions.filter((q) => {
    const icdCode = String(q?.icdCode ?? "").trim();
    // No icdCode on this row → remove it (non-chronic / general questions)
    if (!icdCode) return false;
    // Has icdCode → keep only if it matches one of selected
    return selectedSet.has(icdCode);
  });
}

type Props = {
  isEdit?: boolean;
  isView?: boolean;
};

function splitSelectedConditions(selected: string[]) {
  const icdCodes: string[] = [];
  const conditions: string[] = [];
  for (const raw of selected ?? []) {
    const [code, description] = splitIcdCode(String(raw ?? ""));
    if (code) icdCodes.push(code);
    conditions.push(description || code || String(raw));
  }
  return { icdCodes, conditions };
}

// Add this component above your Careplan component or in a separate file
const ChronicDiseasePopup = ({
  isOpen,
  diseases,
  onCancel,
  onDone,
  btnLoad,
  maxSelect = 0,
  minSelect,
  previousIcdCodes,
}: {
  isOpen: boolean;
  diseases: string[];
  onCancel: () => void;
  onDone: (selected: string[]) => void;
  btnLoad: boolean;
  maxSelect: number;
  /** Defaults to maxSelect, so CCM/PCM still require an exact count. */
  minSelect?: number;
  previousIcdCodes?: string[];
}) => {
  const [tempSelected, setTempSelected] = useState<string[]>([]);
  const minimum = minSelect ?? maxSelect;
  const isRange = minimum !== maxSelect;
  console.log({ maxSelect, minimum });

  if (!isOpen) return null;

  const handleCheckboxChange = (disease: string) => {
    setTempSelected((prev) => {
      if (prev.includes(disease)) {
        return prev.filter((d) => d !== disease);
      } else {
        // Only allow selection if less than maxSelect are already selected
        if (prev.length < maxSelect) {
          return [...prev, disease];
        }
        return prev; // Don't add if already at max
      }
    });
  };

  const handleDone = () => {
    if (tempSelected.length >= minimum && tempSelected.length <= maxSelect) {
      onDone(tempSelected);
    }
  };

  const isDoneDisabled =
    tempSelected.length < minimum || tempSelected.length > maxSelect;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in-0"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative z-50 bg-white rounded-2xl shadow-2xl w-full max-w-md md:max-w-4xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Select Chronic Diseases
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {isRange
                  ? `Choose ${minimum} to ${maxSelect} conditions to continue`
                  : `Choose at least ${maxSelect} condition${maxSelect !== 1 ? "s" : ""} to continue`}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {previousIcdCodes && Array.isArray(previousIcdCodes) && previousIcdCodes?.length > 0 ? (
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Previous ICD codes
              </p>
              <ul className="mt-2 space-y-1">
                {previousIcdCodes.map((code, index) => (
                  <li key={`${code}-${index}`} className="text-sm text-gray-800">
                    {code}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="space-y-3">
            {diseases.map((disease, index) => {
              const isSelected = tempSelected.includes(disease);
              const isDisabled = !isSelected && tempSelected.length >= maxSelect;
              return (
                <label
                  key={index}
                  className={`flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${isDisabled
                    ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-50"
                    : isSelected
                      ? "border-blue-500 bg-indigo-50 shadow-md cursor-pointer"
                      : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50 cursor-pointer"
                    }`}
                >
                  <div className="flex items-center flex-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleCheckboxChange(disease)}
                      className="w-5 h-5 shrink-0 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <span
                      className={`ml-3 font-medium ${isSelected ? "text-indigo-900" : "text-gray-700"
                        }`}
                    >
                      {disease}
                    </span>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="text-blue-600" size={20} />
                  )}
                </label>
              );
            })}
          </div>

          {/* Selection Counter */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-center text-blue-700">
              <span className="font-semibold">{tempSelected.length}</span> of {maxSelect}{" "}
              selected {isRange ? `(${minimum}–${maxSelect})` : "(max)"}
              {tempSelected.length < minimum && (
                <span className="block text-xs mt-1 text-blue-600">
                  Select {minimum - tempSelected.length} more to continue
                </span>
              )}
              {isRange &&
                tempSelected.length >= minimum &&
                tempSelected.length < maxSelect && (
                  <span className="block text-xs mt-1 text-blue-600">
                    You can select {maxSelect - tempSelected.length} more
                  </span>
                )}
              {tempSelected.length === maxSelect && (
                <span className="block text-xs mt-1 text-green-600 font-medium">
                  ✓ Maximum selection reached
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button variant={"outline"} className="w-fit" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            onClick={handleDone}
            disabled={isDoneDisabled || btnLoad}
            className="w-fit"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

// Updated Careplan Component
const Careplan = ({ isEdit = false, isView = false }: Props) => {
  const isMobile = useIsMobile();

  const [isPopupOpen, setIsPopupOpen] = useState(isEdit);
  const [fetchingQuestion, setFetchingQuestion] = useState(false);
  const { state } = useLocation();
  const [searchParam] = useSearchParams();
  const IS_FOLLOW_UP = searchParam.get("type") === "followup";
  const IS_DUPLICATE = searchParam.get("type") === "duplicate";
  const planParam = searchParam.get("plan");
  const IS_PCM = planParam === "pcm";
  const navigate = useNavigate();
  const { patientId, careplanId, id } = useParams();
  const duplicateSourcePlan = IS_DUPLICATE ? (state?.carePlan ?? null) : null;
  const [careplanData, setCareplanData] = useState<any>(
    IS_DUPLICATE ? duplicateSourcePlan : state?.carePlan,
  );


  // const careplanData = state?.carePlan;
  console.log({ careplanData: state });
  const { user } = useAuth();
  const IS_ROLE_CARETAKER = user?.role == "caretaker";

  const [patientData, setPatientData] = useState(
    state?.patientData ? state?.patientData : {},
  );
  const IS_APCM =
    planParam === "apcm" ||
    (!planParam &&
      Array.isArray(patientData?.programs) &&
      patientData.programs.includes("apcm") &&
      !patientData.programs.includes("ccm") &&
      !patientData.programs.includes("pcm"));
  const carePlanBase = IS_APCM
    ? "/apcm-careplan"
    : IS_PCM
      ? "/pcm-careplan"
      : "/careplan";

  const [selectedCaretaker, setSelectedCaretaker] = useState(
    IS_ROLE_CARETAKER
      ? (Array.isArray(patientData?.assignedCaretakerIds)
        ? patientData.assignedCaretakerIds.find(
          (e: any) => e?.email?.toLowerCase() === user?.email?.toLowerCase(),
        )?._id
        : undefined)
      : careplanData?.careTakerId?._id,
  );

  // Check if caretaker is in the assigned caretakers list
  const isCaretakerAssigned = useMemo(() => {
    if (!IS_ROLE_CARETAKER) return true; // Not a caretaker, so always allow
    if (!Array.isArray(patientData?.assignedCaretakerIds)) return false;
    return patientData.assignedCaretakerIds.some(
      (caretaker: any) => caretaker?.email?.toLowerCase() === user?.email?.toLowerCase() || caretaker._id === user?.id || caretaker._id === user?.caretakerId
    );
  }, [IS_ROLE_CARETAKER, patientData?.assignedCaretakerIds, user?.email, user?.id, user?.caretakerId]);


  useEffect(() => {
    if (user?.role === "caretaker") {
      const caretaker = patientData?.assignedCaretakerIds?.find((e: any) => e.email == user.email);
      if (caretaker) setSelectedCaretaker(caretaker?._id);
    }
  }, [user?.role, patientData?.assignedCaretakerIds])

  const [chronicQuestions, setChronicQuestions] = useState<any[]>([]);
  const [btnLoad, setbtnLoad] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const careplanContentRef = useRef<HTMLDivElement>(null);
  const { loading: patientLoading, err: patientErr } = useGetData(
    "/patients/" + patientId,
    {},
    false,
    !!patientId && !state?.patientData,
    (err, data) => {
      setPatientData(data?.data);
    },
  );
  const AllChronicDiseases = [
    patientData?.dx1,
    patientData?.dx2,
    patientData?.dx3,
    patientData?.dx4,
    patientData?.dx5,
  ].filter((v) => v);

  const [selectedChronic, setSelectedChronic] = useState<string[]>(
    IS_DUPLICATE
      ? []
      : IS_PCM
        ? [careplanData?.icdCode || ""].filter(Boolean)
        : (careplanData?.icdCodes || []),
  );
  const icdCodesTopDisplay = useMemo(() => {
    if(IS_PCM) return selectedChronic;
    const arr = (selectedChronic || [])
      .filter((c: any) => c != null && String(c).trim() !== "")
      .map((c: any) => String(c).trim());
    return arr.length > 0 ? arr.join(", ") : "";
  }, [selectedChronic]);

  const duplicatePreviousIcdCodes = useMemo(() => {
    if (!IS_DUPLICATE) return [];
    if (IS_PCM) {
      const code = careplanData?.icdCode;
      return code != null && String(code).trim() !== "" ? [String(code).trim()] : [];
    }
    const codes = careplanData?.icdCodes;
    return Array.isArray(codes)
      ? codes
          .filter((c) => c != null && String(c).trim() !== "")
          .map((c) => String(c).trim())
      : [];
  }, [IS_DUPLICATE, IS_PCM, careplanData?.icdCode, careplanData?.icdCodes]);

  console.log({ selectedChronic });
  const allCategories = groupQuestionsByCategory(
    carePlanQuestions,
    chronicQuestions,
  );

  const formInitialValues = useMemo(
    () =>
      careplanData?.questions
        ? getCareplanValues(
            careplanData.questions,
            allCategories.flatMap((c: any) => c.questions ?? []),
          )
        : {},
    [careplanData?.questions, allCategories],
  );

  // console.log('state?.carePlan?._id', state?.carePlan?._id);
  console.log({ ISVALID: !careplanId && !state?.carePlan?._id })
  const { loading: careplanLoading, err: careplanErr } = useGetData(carePlanBase + "/" + careplanId, {}, false, !!careplanId && !state?.carePlan?._id, (err, data) => {
    if (!err && data) {
      // console.log({state : state?.carePlan , api: data?.data})
      setCareplanData(data?.data);
      setSelectedChronic(IS_PCM ? [data?.data?.icdCode].filter(Boolean) : data?.data?.icdCodes || []);
      if (!IS_ROLE_CARETAKER) {
        setSelectedCaretaker(data?.data?.careTakerId?._id)
      }

    }
  })

  const duplicateSourceId = IS_DUPLICATE
    ? String((duplicateSourcePlan as { _id?: string } | null)?._id ?? "")
    : "";

  const { loading: duplicateSourceLoading } = useGetData(
    carePlanBase + "/" + duplicateSourceId,
    {},
    false,
    IS_DUPLICATE && !!duplicateSourceId,
    (err, data) => {
      if (!err && data?.data) {
        setCareplanData(data.data);
        if (!IS_ROLE_CARETAKER && data.data?.careTakerId?._id) {
          setSelectedCaretaker(data.data.careTakerId._id);
        }
      }
    },
  );

  const getQuestionBySelectedICDCodes = async (selected: string[], cb: any) => {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      setFetchingQuestion(true);
      const questions = await fetchChronicQuestionsFromIcdApi(selected);
      setChronicQuestions(questions);
      cb();
      resolveFn("Questionaire Fetched Successfully");
    } catch (error) {
      rejectFn(error);
    } finally {
      setFetchingQuestion(false);
    }
  };

  const handlePopupCancel = () => {
    setIsPopupOpen(false);
    navigate(-1);
  };

  const handlePopupDone = (selected: string[]) => {
      // alert("handlePopupDone");
    if (IS_DUPLICATE) {
      void handleDuplicatePopupDone(selected);
      return;
    }
    getQuestionBySelectedICDCodes(selected, () => {
      setSelectedChronic(selected as any);
      setIsPopupOpen(false);
    });
  };

  const handleDuplicatePopupDone = async (selected: string[]) => {
    if (duplicateSourceLoading) {
      toast.error("Still loading the original care plan. Please try again in a moment.");
      return;
    }

    const sourceQuestions = filterSourceForDuplicateCareplanQuestions(careplanData?.questions, selected);
  //  console.log("sourceQuestions : -----------------------",{sourceQuestions, selected});
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      setFetchingQuestion(true);
      const enriched = await enrichCareplanChronicQuestions(sourceQuestions, selected);
      // console.log("adter dropdown 1 : -----------------------",{enriched, selected});
      setChronicQuestions(enriched);
      setSelectedChronic(selected);
      setIsPopupOpen(false);
      resolveFn("Duplicate care plan ready. Review your answers and submit.");
    } catch (error) {
      rejectFn(error);
    } finally {
      setFetchingQuestion(false);
    }
  };
  async function handleSubmitCareplan(formdata: any) {
    // Require explicit acknowledgement that this is not an emergency service
    if (!formdata?.emergencyConsent) {
      toast.error(
        "Please acknowledge that Anthro Care is not an emergency service and you will call 911 in an emergency.",
      );
      return;
    }
    let finalData = allCategories
      .map((cat: any) => cat.questions)
      .flat()
      .map((ques: any) => ({
        ...ques,
        answer: formdata?.[ques.questionrandomid]
          ? formdata?.[ques.questionrandomid]
          : ques?.type == "multiple_choice"
            ? []
            : "",
        // Free-text comment per question (not part of selectable answers)
        comment: formdata?.[`${ques.questionrandomid}_comment`] ?? "",
      }));
    console.log({ allCategories, formdata, finalData });
    // return;
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setbtnLoad(true);
      if (!selectedCaretaker && !IS_ROLE_CARETAKER) {
        throw new Error("Caretaker is mandatory");
      }
      const providerId =
        patientData?.assignedProviderId?._id || patientData?.assignedProviderId;
      const { icdCodes, conditions } = splitSelectedConditions(selectedChronic);
      // const carePlanDate = new Date().toLocaleDateString("en-CA");
      let payload = IS_APCM
        ? {
            patientId: patientData?._id,
            careTakerId: selectedCaretaker,
            providerId,
            icdCodes,
            conditions,
            questions: finalData,
            // carePlanDate,
            status: "submitted",
          }
        : {
            patientId: patientData?._id,
            careTakerId: selectedCaretaker,
            icdCodes: selectedChronic,
            icdCode: selectedChronic?.[0],
            questions: finalData,
            status: "submitted",
          };
      if (isEdit && !IS_FOLLOW_UP && !IS_DUPLICATE) {
        const { data } = await AxiosInstance().put(
          carePlanBase + "/" + careplanData?._id,
          payload,
        );

        resolveFn("Careplan updated successfully");
      } else {

        console.log("payload : -----------------------",{payload});
        // alert(IS_PCM ? "pcm" : "careplan");
        // return;
        const { data } = await AxiosInstance().post(carePlanBase, payload);

        resolveFn("Careplan added successfully");
      }
      const profilePatientId = patientData?._id || patientId;
      if (profilePatientId) {
        navigate(
          `/patients/${profilePatientId}?tab=nursing-notes&openNursingNote=1`,
          { replace: true },
        );
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.log(error);
      rejectFn(error);
    } finally {
      setbtnLoad(false);
    }
  }

  useEffect(() => {
    // Follow-up prefills ICDs from the prior plan; duplicate uses the chronic popup first.
    if (
      patientData &&
      Object.keys(patientData).length > 0 &&
      !isView &&
      !IS_FOLLOW_UP
    ) {
      setIsPopupOpen(true);
    }
  }, [patientData, isView, IS_FOLLOW_UP]);
  // console.log("----", {
  //   patientData,
  //   careplanData,
  //   selectedChronic,
  //   allCategories,
  //   chronicQuestions
  // });

  useEffect(() => {
    // View mode: ViewCareplan enriches chronic ICD fields itself
    if (!(isEdit || IS_FOLLOW_UP)) return;

    const icdCodes = (
      (selectedChronic?.length ? selectedChronic : careplanData?.icdCodes) ?? []
    ).filter((c: any) => c != null && String(c).trim() !== "");

    if (!icdCodes.length || !Array.isArray(careplanData?.questions)) return;

    let cancelled = false;

    const loadEnrichedChronic = async () => {
      try {
        setFetchingQuestion(true);
        // console.log('icdCodes inside loadEnrichedChronic', careplanData , " - ", icdCodes);
        
        const enriched = await enrichCareplanChronicQuestions(
          careplanData.questions,
          icdCodes,
        );
        // console.log("enriched  -----------------------------", enriched);
        if (!cancelled) setChronicQuestions(enriched);
      } catch {
        // console.log("error from the ");
        if (!cancelled) {
          setChronicQuestions(
            careplanData.questions.filter(
              (q: any) =>
                q?.questionrandomid?.includes("chronic") ||
                String(q?.category ?? "")
                  .toLowerCase()
                  .includes("chronic"),
            ),
          );
        }
      } finally {
        if (!cancelled) setFetchingQuestion(false);
      }
    };

    void loadEnrichedChronic();
    return () => {
      cancelled = true;
    };
  }, [
    isEdit,
    IS_FOLLOW_UP,
    careplanData?._id,
    careplanData?.questions,
    careplanData?.icdCodes,
    selectedChronic,
  ]);

  const handleDownloadPdf = async () => {
    const id = careplanData?._id;
    if (!id) return;
    setIsDownloading(true);
    try {
      await downloadBackendPdf({
        url: `${carePlanBase}/${id}/pdf`,
        fallbackFilename: "care-plan.pdf",
      });
    } catch (error) {
      getErrToast(error);
    } finally {
      setIsDownloading(false);
    }
  };
  console.log({careplanData})

  // Load image and convert to base64

  return (
    <DashboardLayout activePage={isView ? "view-careplan" : "careplan"} rightcomponent={<>
      {careplanData && isView && (
        <Button
          variant="outline"
          className="w-auto"
          onClick={handleDownloadPdf}
          disabled={isDownloading}
        >
          <Download className="w-4 h-4" />
          {!isMobile && (isDownloading ? "Preparing PDF..." : "PDF")}
        </Button>
      )}
    </>}>
      {/* Chronic Disease Selection Popup */}
      {!isView && !isEdit && (
        <ChronicDiseasePopup
          isOpen={isEdit ? false : isPopupOpen}
          diseases={AllChronicDiseases}
          onCancel={handlePopupCancel}
          onDone={handlePopupDone}
          btnLoad={fetchingQuestion || (IS_DUPLICATE && duplicateSourceLoading)}
          previousIcdCodes={
            IS_DUPLICATE && duplicatePreviousIcdCodes.length > 0
              ? duplicatePreviousIcdCodes
              : undefined
          }
          minSelect={IS_APCM ? 1 : undefined}
          maxSelect={
            IS_APCM
              ? 5
              : Array.isArray(patientData?.programs)
                ? patientData.programs.includes("ccm")
                  ? 2
                  : patientData.programs.includes("pcm")
                    ? 1
                    : patientData.programs.includes("apcm")
                      ? 4
                      : 0
                : 0
          }
        />
      )}
      {/* {JSON.stringify(patientData?.programs)}  */}

      <div className="flex flex-col gap-4">
        <div className="shrink flex justify-end">



          {!isView && (
            <div className="space-y-1 w-full sm:w-60">
              <Label>Select Caretaker</Label>
              <Select
                value={selectedCaretaker}
                onValueChange={setSelectedCaretaker}
                disabled={
                  isView ||
                  (patientData?.assignedCaretakerIds || []).length == 0 ||
                  IS_ROLE_CARETAKER
                }
              >
                <SelectTrigger className="">
                  <SelectValue placeholder={"Select caretaker"} />
                </SelectTrigger>
                <SelectContent className="">
                  {(patientData?.assignedCaretakerIds || []).map(
                    (opt: any) => (
                      <SelectItem value={opt?._id} className="">
                        {opt.firstName} {opt.lastName}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

        </div>
        {!isView ? (
          <div className="rounded-md border border-[#dddddd] bg-[#eeeeee] p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="font-semibold text-[#6E6E6E] text-sm">PATIENT NAME:</span>
                <span className="ml-2 text-sm text-black">
                  {[patientData?.firstName, patientData?.middleName, patientData?.lastName]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] text-sm">DATE OF BIRTH:</span>
                <span className="ml-2 text-sm text-black">
                  {patientData?.dateOfBirth ? formatDate(patientData.dateOfBirth) : "—"}
                </span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] text-sm">Organization:</span>
                <span className="ml-2 text-sm text-black">
                  {patientData?.organizationId?.organizationName || "—"}
                </span>
              </div>
            </div>

            {icdCodesTopDisplay ? (
              <div className="mt-4 pt-3 border-t border-[#dddddd]">
                <span className="font-semibold text-[#6E6E6E] text-sm">ICD CODES:</span>
                <span className="ml-2 text-sm text-black">{icdCodesTopDisplay}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* {chronicQuestions.length > 0 && ( */}

        {isView && (
          <LoaderWrapper loading={careplanLoading && !careplanData} error={careplanErr}>
            {careplanData && <ViewCareplan carePlan={careplanData} isPcm={IS_PCM} />}
          </LoaderWrapper>
        )}

        {!isView && <>
          {(isView
            ? isView
            : patientData && Object.keys(patientData).length > 0) &&
            (isEdit || isView ? true : selectedChronic.length > 0) && (
              <div
                className="flex-1 overflow-y-auto "
                ref={careplanContentRef}
                data-careplan-content
              >
                <RHFWrapper
                  validateSchema={{}}
                  initialValues={formInitialValues}
                  isValidate={false}
                  onSubmit={(data) => handleSubmitCareplan(data)}
                  className="space-y-4"
                >
                  {allCategories
                    .filter(Boolean)
                    .map((category: any, catIdx: number) => {
                      const icdKey =
                        category.icdCodePart != null
                          ? String(category.icdCodePart)
                          : "default";
                      return (
                        <div
                          key={`${category.heading}-${icdKey}-${catIdx}`}
                          className="p-4 border rounded-xl space-y-4"
                        >
                          {category.icdCodePart != null ? (
                            <div className="space-y-1 border-b border-border pb-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Chronic conditions
                              </p>
                              <h2 className="text-lg font-semibold text-foreground">
                                ICD Code: {category.icdCodePart}
                              </h2>
                              {category.icdSectionTitle &&
                                category.icdSectionTitle !== category.icdCodePart && (
                                  <p className="text-sm text-muted-foreground">
                                    {category.icdSectionTitle}
                                  </p>
                                )}
                            </div>
                          ) : (
                            <h2 className="text-xl font-semibold">
                              {category.heading}
                            </h2>
                          )}
                          <div className="space-y-4">
                            {category.questions.map((question: any) => {
                              const commentName = `${question?.questionrandomid}_comment`;
                              // Free-text comment field should not affect selectable answers

                              if (question.type == "single_input") {
                                return (
                                  <div
                                    key={question?.questionrandomid}
                                    className="space-y-2"
                                  >
                                    <RhfInput
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      type=""
                                      disabled={isView}
                                    />
                                    <RhfInput
                                      name={commentName}
                                      label="Comment"
                                      type="textarea"
                                      disabled={isView}
                                    />
                                  </div>
                                );
                              } else if (question.type == "textarea") {
                                return (
                                  <div
                                    key={question?.questionrandomid}
                                    className="space-y-2"
                                  >
                                    <RhfInput
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      type="textarea"
                                      disabled={isView}
                                    />
                                    {/* <RhfInput
                                    name={commentName}
                                    label="Comments"
                                    type="textarea"
                                    disabled={isView}
                                  /> */}
                                  </div>
                                );
                              } else if (question.type == "multi_input") {
                                return (
                                  <div
                                    key={question?.questionrandomid}
                                    className="space-y-2"
                                  >
                                    <RhfMultiInput
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      disabled={isView}
                                    />
                                    <RhfInput
                                      name={commentName}
                                      label="Comments"
                                      type="textarea"
                                      disabled={isView}
                                    />
                                  </div>
                                );
                              } else if (question.type == "single_select") {
                                return (
                                  <div
                                    key={question?.questionrandomid}
                                    className="space-y-2"
                                  >
                                    <RhfSelect
                                      options={question.options}
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      disabled={isView}
                                    />
                                    <RhfInput
                                      name={commentName}
                                      label="Comments"
                                      type="textarea"
                                      disabled={isView}
                                    />
                                  </div>
                                );
                              } else if (question.type == "multi_select") {
                                return (
                                  <div
                                    key={question?.questionrandomid}
                                    className="space-y-2"
                                  >
                                    <RhfSelect
                                      options={question.options}
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      multiple
                                      disabled={isView}
                                    />
                                    <RhfInput
                                      name={commentName}
                                      label="Comments"
                                      type="textarea"
                                      disabled={isView}
                                    />
                                  </div>
                                );
                              } else if (question.type == "single_choice") {
                                return (
                                  <SingleChoiceQuestion
                                    key={question?.questionrandomid}
                                    question={question}
                                    isView={isView}
                                  />
                                );
                              } else if (question.type == "multiple_choice") {
                                return (

                                  <MultiChoiceQuestion
                                    key={question?.questionrandomid}
                                    question={question}
                                    isView={isView} />
                                  // <div
                                  //   key={question?.questionrandomid}
                                  //   className="space-y-2"
                                  // >
                                  //   <MultiChoice
                                  //     question={question}
                                  //     isView={isView}
                                  //   />

                                  //  {!question?.hideCommnetSection &&
                                  //   <RhfInput
                                  //     name={commentName}
                                  //     label="Comments"
                                  //     type="textarea"
                                  //     disabled={isView}
                                  //   />}
                                  // </div>
                                );
                              } else return null;
                            })}
                          </div>
                        </div>
                      );
                    })}
                  {/* Emergency service consent */}
                  <div className="p-4 border rounded-xl space-y-3 bg-muted/30">
                    <p className="text-sm text-muted-foreground">
                      We are not an emergency service, so at any point you feel you need
                      to seek medical attention immediately, we advise you to call 911.
                    </p>
                    <RhfChecked
                      name="emergencyConsent"
                      label=""
                      options={[
                        {
                          label:
                            "I understand that Anthro Care is not an emergency service and I will call 911 if I need immediate medical attention.",
                          value: "acknowledged",
                        },
                      ]}
                      required
                    />
                  </div>
                  {!isView && (
                    <Button type="submit" disabled={btnLoad || !isCaretakerAssigned}>
                      Submit
                    </Button>
                  )}
                </RHFWrapper>
              </div>
            )}
        </>}
        {/* )} */}
      </div>
    </DashboardLayout>
  );
};
export default Careplan;


const ConditionalCommentField = ({
  name,
  show,
  label = "Comments",
  disabled = false,
}: {
  name: string;
  show: boolean;
  label?: string;
  disabled?: boolean;
}) => {
  const { setValue } = useFormContext();

  useEffect(() => {
    if (!show) {
      setValue(name, "");
    }
  }, [name, show, setValue]);

  if (!show) return null;

  return (
    <RhfInput
      name={name}
      label={label}
      type="textarea"
      disabled={disabled}
    />
  );
};

const MultiChoiceQuestion = ({ question, isView }: any) => {
  const value = useWatch({ name: question?.questionrandomid });
  console.log(value);

  const commentName = `${question?.questionrandomid}_comment`;

  let isOtherOptionSelected = value && Array.isArray(value) && value?.find((opt: any) => String(opt).toLowerCase() === "other") ? true : false;

  // let hideCommnetSection = question?.hideCommnetSection ? true : false; 


  // ler showComment =

  console.log({ isOtherOptionSelected }, "!question?.hideCommnetSection", !question?.hideCommnetSection);

  return (
    <div className="space-y-2">
      <MultiChoice
        question={question}
        isView={isView}
      />

      <ConditionalCommentField
        name={commentName}
        show={isOtherOptionSelected}
        disabled={isView}
      />
    </div>
  );
};


const SingleChoiceQuestion = ({ question, isView }: any) => {
  const value = useWatch({ name: question?.questionrandomid });
  console.log(value);
  const commentName = `${question?.questionrandomid}_comment`;

  const showComment = Boolean(
    question?.openCommetonOptions?.find(
      (opt: any) => String(opt).toLowerCase() === String(value).toLowerCase(),
    ),
  );

  return (
    <div className="space-y-2">
      <SingleChoice question={question} isView={isView} />

      <ConditionalCommentField
        name={commentName}
        show={showComment}
        disabled={isView}
      />
    </div>
  );
};

const SingleChoice = ({ question, isView }: any) => {
  // const value = useWatch({ name: question?.questionrandomid });
  // const IS_INC =
  //   question.options &&
  //   Array.isArray(question.options) &&
  //   question.options.includes(value);

  // Ensure Yes/No/Other options; remove separate "Other" text + input
  const baseOptions = Array.isArray(question.options) ? question.options : [];
  const hasYes = baseOptions.some((opt: any) =>
    String(typeof opt === "string" ? opt : opt?.label || opt?.value)
      .toLowerCase()
      .includes("yes"),
  );
  const hasNo = baseOptions.some((opt: any) =>
    String(typeof opt === "string" ? opt : opt?.label || opt?.value)
      .toLowerCase()
      .includes("no"),
  );
  // const hasOther = baseOptions.some((opt: any) =>
  //   String(typeof opt === "string" ? opt : opt?.label || opt?.value)
  //     .toLowerCase()
  //     .includes("other"),
  // );

  // Fix in fucture...
  const normalizedOptions: any[] = [
    ...(hasYes ? [] : ["Yes"]),
    ...(hasNo ? [] : ["No"]),
    ...baseOptions,
    // ...(hasOther ? [] : ["Other"]),
  ];

  return (
    <div className="flex flex-col gap-1">
      <RhfChecked
        key={question?.questionrandomid}
        options={normalizedOptions}
        name={question?.questionrandomid}
        label={question?.question}
        disabled={isView}
      />
    </div>
  );
};

const MultiChoice = ({ question, isView }: any) => {
  const value = useWatch({ name: question?.questionrandomid });
  const IS_INC =
    question.options &&
    Array.isArray(question.options) &&
    question.options.includes(value);

  // Add "Other" as an option instead of separate text + input
  const baseOptions = Array.isArray(question.options) ? question.options : [];
  const hasOther = baseOptions.some((opt: any) =>
    String(typeof opt === "string" ? opt : opt?.label || opt?.value)
      .toLowerCase()
      .includes("other"),
  );
  const normalizedOptions: any[] = [...baseOptions, ...(hasOther ? [] : ["Other"])];
  return (
    <div className="flex flex-col gap-1">
      <RhfChecked
        key={question?.questionrandomid}
        options={normalizedOptions}
        name={question?.questionrandomid}
        label={question?.question}
        multiple
        disabled={isView}
      />
    </div>
  );
};


